import React, { useState, useEffect, useRef } from 'react';
import { costAPI } from '../services/api';

const PROVIDER_META = {
  gcp:          { name: 'Google Cloud Platform', short: 'GCP', color: '#4285f4', bg: '#e8f0fe', billing: 'https://console.cloud.google.com/billing' },
  azure:        { name: 'Microsoft Azure',        short: 'AZU', color: '#0078d4', bg: '#ddf0ff', billing: 'https://portal.azure.com/#blade/Microsoft_Azure_Billing/BillingMenuBlade/Overview' },
  aws:          { name: 'Amazon Web Services',    short: 'AWS', color: '#e67e00', bg: '#fff3e0', billing: 'https://console.aws.amazon.com/billing/home' },
  digitalocean: { name: 'DigitalOcean',           short: 'DO',  color: '#0069ff', bg: '#e6f0ff', billing: 'https://cloud.digitalocean.com/account/billing' },
  vultr:        { name: 'Vultr',                  short: 'VUL', color: '#007bfc', bg: '#e5f0ff', billing: 'https://my.vultr.com/billing/' },
  lightnode:    { name: 'LightNode',              short: 'LN',  color: '#00913a', bg: '#e6f5ec', billing: 'https://console.lightnode.com/billing' },
};

function fmt(n, d = 4) { return Number(n || 0).toFixed(d); }
function fmtDate(d, o) { return new Date(d).toLocaleDateString('en-US', o); }
function printSingleInvoice(inv) {
  const w = window.open('', '_blank', 'width=650,height=800');
  w.document.write(`
    <html><head><title>Invoice - ${inv.provider} - ${inv.description}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 40px; color: #111; }
      h1 { font-size: 20px; margin-bottom: 4px; }
      .row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee; }
      .label { color: #666; font-size: 13px; }
      .value { font-weight: 700; font-size: 13px; }
      .total { font-size: 22px; font-weight: 900; margin-top: 20px; text-align: right; }
      .status { display: inline-block; padding: 3px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; }
    </style></head><body>
    <h1>CloudUnify</h1>
    <div style="color:#666;font-size:12px;margin-bottom:24px;">Cloud Provider Invoice</div>
    <div class="row"><span class="label">Provider</span><span class="value">${inv.provider.toUpperCase()}</span></div>
    <div class="row"><span class="label">Description</span><span class="value">${inv.description}</span></div>
    <div class="row"><span class="label">Date</span><span class="value">${new Date(inv.date).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</span></div>
    <div class="row"><span class="label">Status</span><span class="status" style="background:${inv.status==='PAID'?'#d1fae5':'#fef3c7'};color:${inv.status==='PAID'?'#059669':'#d97706'};">${inv.status}</span></div>
    <div class="total">Total: $${inv.amount.toFixed(2)}</div>
    <div style="margin-top:40px;font-size:11px;color:#999;">Generated by CloudUnify. This is a summary, not an official invoice from ${inv.provider}. For the official document, visit your ${inv.provider} billing portal.</div>
    </body></html>
  `);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

export default function Invoice() {
  const [costs,   setCosts]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [days,    setDays]    = useState(30);
  const [prevInvoices, setPrevInvoices] = useState([]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    costAPI.list({ days })
      .then(r => setCosts(r.data.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);
  useEffect(() => {
    costAPI.invoices().then(r => setPrevInvoices(r.data.data || [])).catch(() => {});
  }, []);

  const now       = new Date();
  const startDate = new Date(now - days * 86400000);
  const period    = `${fmtDate(startDate,{month:'short',day:'numeric',year:'numeric'})} \u2013 ${fmtDate(now,{month:'short',day:'numeric',year:'numeric'})}`;
  const invoiceNo = `INV-${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  const dueDate   = new Date(now.getTime() + 14 * 86400000);

  const providers    = (costs?.byProvider || []).filter(p => p.isConnected);
  const gcpInstances = costs?.gcpInstances || [];
  const grandTotal   = costs?.grandTotal || 0;
  const subtotal     = providers.reduce((s, p) => s + (p.total || 0), 0);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', flexDirection:'column', gap:16 }}>
      <div className="spinner" />
      <div style={{ color:'#8899b4', fontSize:14 }}>Fetching live costs from all providers...</div>
    </div>
  );

  if (error) return (
    <div style={{ padding:40, textAlign:'center', color:'#ef4444' }}>
      <div style={{ fontSize:32, marginBottom:12 }}>Warning</div>
      <div style={{ fontSize:16 }}>Failed to load costs: {error}</div>
    </div>
  );

  return (
    <>
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        @media print {
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          .cu-no-print { display: none !important; }
          .cu-invoice-wrap { box-shadow: none !important; border-radius: 0 !important; margin: 0 !important; width: 100% !important; }
          body > * { visibility: hidden; }
          .cu-print-root { visibility: visible !important; position: fixed; top: 0; left: 0; width: 210mm; }
          .cu-print-root * { visibility: visible !important; }
        }
      `}</style>

      <div className="cu-no-print" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, color:'#f0f4ff', margin:0 }}>Unified Invoice</h1>
          <p style={{ fontSize:13, color:'#8899b4', margin:'4px 0 0' }}>A4-ready bill across all your cloud providers</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <span style={{ fontSize:12, color:'#8899b4' }}>Period:</span>
          {[7,14,30,90].map(d => (
            <button key={d} className={`btn btn-sm ${days===d?'btn-primary':'btn-secondary'}`} onClick={() => setDays(d)}>{d}d</button>
          ))}
          <button className="btn btn-sm btn-primary" onClick={() => window.print()} style={{ marginLeft:8 }}>
            Print / Save PDF
          </button>
        </div>
      </div>

      <div className="cu-print-root">
        <div className="cu-invoice-wrap" style={{
          background:'#ffffff', color:'#111111',
          width:'210mm', minHeight:'297mm',
          margin:'0 auto', padding:'12mm 14mm',
          fontFamily:"'Helvetica Neue', Arial, sans-serif",
          fontSize:'10.5pt', lineHeight:1.55,
          boxSizing:'border-box',
          boxShadow:'0 8px 48px rgba(0,0,0,0.35)',
          borderRadius:6,
        }}>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', paddingBottom:'7mm', borderBottom:'2.5px solid #111', marginBottom:'7mm' }}>
            <div>
              <div style={{ fontSize:'22pt', fontWeight:900, letterSpacing:'-0.5px', color:'#111', lineHeight:1.1 }}>CloudUnify</div>
              <div style={{ fontSize:'8.5pt', color:'#666', marginTop:3 }}>Multi-Cloud Management Platform</div>
              <div style={{ fontSize:'8.5pt', color:'#666' }}>cloudunify.run.place</div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:'15pt', fontWeight:800, color:'#111', letterSpacing:'1px' }}>TAX INVOICE</div>
              <div style={{ fontSize:'8.5pt', color:'#555', marginTop:5, lineHeight:1.8 }}>
                <span style={{ color:'#111', fontWeight:700 }}>Invoice No: </span>{invoiceNo}<br/>
                <span style={{ color:'#111', fontWeight:700 }}>Issue Date: </span>{fmtDate(now,{month:'long',day:'numeric',year:'numeric'})}<br/>
                <span style={{ color:'#111', fontWeight:700 }}>Due Date: </span>{fmtDate(dueDate,{month:'long',day:'numeric',year:'numeric'})}
              </div>
              <div style={{ marginTop:8, display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', border:'1.5px solid #16a34a', borderRadius:20, fontSize:'7.5pt', color:'#16a34a', fontWeight:700 }}>
                <span style={{ width:6, height:6, borderRadius:'50%', background:'#16a34a', display:'inline-block' }}></span>
                ACTIVE
              </div>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'7mm', marginBottom:'7mm' }}>
            <div style={{ background:'#f7f7f7', padding:'4mm 5mm', borderRadius:5, border:'0.5px solid #e0e0e0' }}>
              <div style={{ fontSize:'7.5pt', fontWeight:800, color:'#999', letterSpacing:'0.08em', marginBottom:5 }}>BILLED BY</div>
              <div style={{ fontWeight:800, fontSize:'11pt', color:'#111' }}>CloudUnify</div>
              <div style={{ fontSize:'8.5pt', color:'#555', marginTop:2 }}>cloudunify.run.place</div>
              <div style={{ fontSize:'8.5pt', color:'#555' }}>Multi-Cloud Management Platform</div>
            </div>
            <div style={{ background:'#f7f7f7', padding:'4mm 5mm', borderRadius:5, border:'0.5px solid #e0e0e0' }}>
              <div style={{ fontSize:'7.5pt', fontWeight:800, color:'#999', letterSpacing:'0.08em', marginBottom:5 }}>BILLING PERIOD</div>
              <div style={{ fontWeight:800, fontSize:'11pt', color:'#111' }}>{period}</div>
              <div style={{ fontSize:'8.5pt', color:'#555', marginTop:2 }}>Duration: {days} days</div>
              <div style={{ fontSize:'8.5pt', color:'#555' }}>Currency: USD</div>
            </div>
          </div>

          <div style={{ fontSize:'8pt', fontWeight:800, color:'#999', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'3mm' }}>
            Charges by Cloud Provider
          </div>

          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'9.5pt', marginBottom:'6mm' }}>
            <thead>
              <tr style={{ background:'#111', color:'#fff' }}>
                <th style={{ padding:'3mm 3.5mm', textAlign:'left', fontWeight:700, fontSize:'7.5pt', letterSpacing:'0.06em' }}>PROVIDER</th>
                <th style={{ padding:'3mm 3.5mm', textAlign:'left', fontWeight:700, fontSize:'7.5pt', letterSpacing:'0.06em' }}>DESCRIPTION</th>
                <th style={{ padding:'3mm 3.5mm', textAlign:'center', fontWeight:700, fontSize:'7.5pt', letterSpacing:'0.06em' }}>STATUS</th>
                <th style={{ padding:'3mm 3.5mm', textAlign:'center', fontWeight:700, fontSize:'7.5pt', letterSpacing:'0.06em' }}>PERIOD</th>
                <th style={{ padding:'3mm 3.5mm', textAlign:'right', fontWeight:700, fontSize:'7.5pt', letterSpacing:'0.06em' }}>AMOUNT (USD)</th>
                <th className="cu-no-print" style={{ padding:'3mm 3.5mm', textAlign:'center', fontWeight:700, fontSize:'7.5pt', letterSpacing:'0.06em' }}>PAY NOW</th>
              </tr>
            </thead>
            <tbody>
              {providers.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding:'8mm', textAlign:'center', color:'#888', fontStyle:'italic' }}>
                    No connected cloud providers. Connect providers in the Clouds section.
                  </td>
                </tr>
              )}
              {providers.map((p, i) => {
                const meta = PROVIDER_META[p.provider] || { name:p.provider, short:p.provider.toUpperCase(), color:'#555', bg:'#f0f0f0', billing:'#' };
                const isDue = p.total > 0;
                return (
                  <React.Fragment key={p.provider}>
                    <tr style={{ background: i % 2 === 0 ? '#fafafa' : '#fff', borderBottom:'0.5px solid #e8e8e8' }}>
                      <td style={{ padding:'4mm 3.5mm', verticalAlign:'middle' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                          <div style={{
                            width:32, height:32, borderRadius:6,
                            background: meta.bg, border: `1.5px solid ${meta.color}55`,
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:'7pt', fontWeight:900, color: meta.color, flexShrink:0,
                          }}>{meta.short}</div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:'9.5pt', color:'#111' }}>{meta.name}</div>
                            <div style={{ fontSize:'7.5pt', color:'#999', marginTop:1 }}>{meta.short} · Cloud Provider</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:'4mm 3.5mm', verticalAlign:'middle', color:'#555', fontSize:'8.5pt' }}>
                        Cloud compute &amp; services<br/>
                        <span style={{ color:'#aaa', fontSize:'8pt' }}>Last {days} days usage</span>
                      </td>
                      <td style={{ padding:'4mm 3.5mm', textAlign:'center', verticalAlign:'middle' }}>
                        <span style={{
                          display:'inline-block', padding:'3px 9px', borderRadius:4, fontSize:'7.5pt', fontWeight:800,
                          background: isDue ? '#fef3c7' : '#dcfce7',
                          color: isDue ? '#92400e' : '#166534',
                          border: `1px solid ${isDue ? '#fbbf24' : '#86efac'}`,
                        }}>{isDue ? 'DUE' : 'PAID'}</span>
                      </td>
                      <td style={{ padding:'4mm 3.5mm', textAlign:'center', verticalAlign:'middle', color:'#666', fontSize:'8.5pt', lineHeight:1.4 }}>
                        {fmtDate(startDate,{month:'short',day:'numeric'})} &ndash;<br/>
                        {fmtDate(now,{month:'short',day:'numeric',year:'numeric'})}
                      </td>
                      <td style={{ padding:'4mm 3.5mm', textAlign:'right', verticalAlign:'middle' }}>
                        <div style={{ fontSize:'12pt', fontWeight:900, color: isDue ? '#111' : '#16a34a' }}>
                          ${fmt(p.total, 4)}
                        </div>
                        <div style={{ fontSize:'7.5pt', color:'#aaa', marginTop:1 }}>USD</div>
                      </td>
                      <td className="cu-no-print" style={{ padding:'4mm 3.5mm', textAlign:'center', verticalAlign:'middle' }}>
                        <a href={meta.billing} target="_blank" rel="noopener noreferrer"
                          style={{
                            display:'inline-block', padding:'7px 14px', borderRadius:6,
                            background: isDue ? meta.color : '#e5e5e5',
                            color: isDue ? '#fff' : '#999',
                            fontSize:'8pt', fontWeight:700, textDecoration:'none',
                            border: `1.5px solid ${isDue ? meta.color : '#ccc'}`,
                            opacity: isDue ? 1 : 0.6, whiteSpace:'nowrap',
                          }}>
                          {isDue ? `Pay on ${meta.short} \u2192` : 'No Charges'}
                        </a>
                      </td>
                    </tr>

                    {p.provider === 'gcp' && gcpInstances.length > 0 && (
                      <tr style={{ background:'#edf4ff', borderBottom:'0.5px solid #c5d9f5' }}>
                        <td colSpan={6} style={{ padding:'3mm 3.5mm 3.5mm 11mm' }}>
                          <div style={{ fontSize:'7.5pt', fontWeight:800, color:'#4285f4', marginBottom:5, letterSpacing:'0.06em' }}>VM BREAKDOWN</div>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'8pt' }}>
                            <thead>
                              <tr style={{ color:'#666', borderBottom:'0.5px solid #c5d9f5' }}>
                                <td style={{ padding:'2px 5px 3px 0', fontWeight:700 }}>Instance</td>
                                <td style={{ padding:'2px 5px 3px', fontWeight:700 }}>Machine Type</td>
                                <td style={{ padding:'2px 5px 3px', fontWeight:700, textAlign:'center' }}>Status</td>
                                <td style={{ padding:'2px 5px 3px', fontWeight:700, textAlign:'center' }}>Zone</td>
                                <td style={{ padding:'2px 5px 3px', fontWeight:700, textAlign:'center' }}>Uptime</td>
                                <td style={{ padding:'2px 5px 3px', fontWeight:700, textAlign:'center' }}>Rate/hr</td>
                                <td style={{ padding:'2px 5px 3px', fontWeight:700, textAlign:'right' }}>Cost (USD)</td>
                              </tr>
                            </thead>
                            <tbody>
                              {gcpInstances.map(vm => (
                                <tr key={vm.instanceId} style={{ borderTop:'0.5px solid #d0e2f8' }}>
                                  <td style={{ padding:'3px 5px 3px 0', fontWeight:700, color:'#111' }}>{vm.name}</td>
                                  <td style={{ padding:'3px 5px', color:'#444', fontFamily:'monospace', fontSize:'7.5pt' }}>{vm.machineType}</td>
                                  <td style={{ padding:'3px 5px', textAlign:'center' }}>
                                    <span style={{ fontWeight:700, fontSize:'7.5pt', color: vm.status === 'running' ? '#16a34a' : '#888' }}>
                                      {vm.status === 'running' ? 'Running' : 'Stopped'}
                                    </span>
                                  </td>
                                  <td style={{ padding:'3px 5px', textAlign:'center', color:'#666', fontSize:'7.5pt' }}>
                                    {(vm.zone || '').split('/').pop() || vm.zone}
                                  </td>
                                  <td style={{ padding:'3px 5px', textAlign:'center', color:'#555' }}>{vm.uptimeHours}h</td>
                                  <td style={{ padding:'3px 5px', textAlign:'center', fontFamily:'monospace', color:'#555', fontSize:'7.5pt' }}>${vm.hourlyRate}</td>
                                  <td style={{ padding:'3px 5px', textAlign:'right', fontWeight:800, color:'#111', fontFamily:'monospace' }}>${fmt(vm.totalCost,4)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'6mm' }}>
            <table style={{ width:'72mm', fontSize:'9.5pt', borderCollapse:'collapse' }}>
              <tbody>
                <tr style={{ borderBottom:'0.5px solid #e0e0e0' }}>
                  <td style={{ padding:'2mm 3mm', color:'#555' }}>Subtotal</td>
                  <td style={{ padding:'2mm 3mm', textAlign:'right', fontFamily:'monospace', fontWeight:600 }}>${fmt(subtotal,4)}</td>
                </tr>
                <tr style={{ borderBottom:'1.5px solid #ccc' }}>
                  <td style={{ padding:'2mm 3mm', color:'#555' }}>Tax / VAT</td>
                  <td style={{ padding:'2mm 3mm', textAlign:'right', color:'#aaa', fontSize:'8pt' }}>billed by provider</td>
                </tr>
                <tr>
                  <td style={{ padding:'3mm 3mm', fontWeight:900, fontSize:'12pt', color:'#111' }}>TOTAL DUE</td>
                  <td style={{ padding:'3mm 3mm', textAlign:'right', fontWeight:900, fontSize:'14pt', fontFamily:'monospace', color:'#111' }}>${fmt(grandTotal,4)}</td>
                </tr>
                <tr>
                  <td colSpan={2} style={{ padding:'1mm 3mm', textAlign:'right', fontSize:'7.5pt', color:'#aaa' }}>USD &middot; {period}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {providers.filter(p => p.total > 0).length > 0 && (
            <div style={{ marginBottom:'6mm', border:'1.5px solid #fbbf24', borderRadius:6, overflow:'hidden' }}>
              <div style={{ background:'#fffbeb', padding:'3mm 4mm', borderBottom:'1px solid #fde68a' }}>
                <div style={{ fontSize:'7.5pt', fontWeight:800, color:'#92400e', letterSpacing:'0.08em' }}>PAY ON EACH PROVIDER PORTAL</div>
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'9pt' }}>
                <thead>
                  <tr style={{ background:'#fef9ec', borderBottom:'0.5px solid #fde68a' }}>
                    <th style={{ padding:'2.5mm 4mm', textAlign:'left', fontWeight:700, color:'#666', fontSize:'7.5pt' }}>PROVIDER</th>
                    <th style={{ padding:'2.5mm 4mm', textAlign:'left', fontWeight:700, color:'#666', fontSize:'7.5pt' }}>BILLING PORTAL URL</th>
                    <th style={{ padding:'2.5mm 4mm', textAlign:'right', fontWeight:700, color:'#666', fontSize:'7.5pt' }}>AMOUNT DUE</th>
                    <th className="cu-no-print" style={{ padding:'2.5mm 4mm', textAlign:'center', fontWeight:700, color:'#666', fontSize:'7.5pt' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {providers.filter(p => p.total > 0).map((p, i, arr) => {
                    const meta = PROVIDER_META[p.provider] || {};
                    return (
                      <tr key={p.provider} style={{ borderBottom: i < arr.length-1 ? '0.5px solid #fde68a' : 'none', background:'#fff' }}>
                        <td style={{ padding:'3mm 4mm', fontWeight:700, color: meta.color || '#111', fontSize:'9pt' }}>
                          <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                            <span style={{
                              display:'inline-block', width:22, height:22, borderRadius:4,
                              background: meta.bg, border:`1px solid ${meta.color}44`,
                              textAlign:'center', lineHeight:'22px',
                              fontSize:'6pt', fontWeight:900, color: meta.color,
                            }}>{meta.short}</span>
                            {meta.name || p.provider}
                          </span>
                        </td>
                        <td style={{ padding:'3mm 4mm', color:'#2563eb', fontSize:'8pt', fontFamily:'monospace' }}>{meta.billing}</td>
                        <td style={{ padding:'3mm 4mm', textAlign:'right', fontWeight:800, fontFamily:'monospace', fontSize:'10pt', color:'#111' }}>${fmt(p.total,4)}</td>
                        <td className="cu-no-print" style={{ padding:'3mm 4mm', textAlign:'center' }}>
                          <a href={meta.billing} target="_blank" rel="noopener noreferrer"
                            style={{
                              display:'inline-block', padding:'6px 14px', borderRadius:5,
                              background: meta.color, color:'#fff',
                              fontSize:'8pt', fontWeight:700, textDecoration:'none',
                            }}>
                            Pay on {meta.short} &rarr;
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {prevInvoices.length > 0 && (
            <div style={{ marginBottom:'5mm' }}>
              <div style={{ fontWeight:800, color:'#333', marginBottom:6, fontSize:'9pt' }}>Previous Bills</div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'8pt' }}>
                <thead>
                  <tr style={{ background:'#f3f4f6' }}>
                    <th style={{ textAlign:'left', padding:'2mm 3mm', color:'#666' }}>Provider</th>
                    <th style={{ textAlign:'left', padding:'2mm 3mm', color:'#666' }}>Description</th>
                    <th style={{ textAlign:'left', padding:'2mm 3mm', color:'#666' }}>Date</th>
                    <th style={{ textAlign:'left', padding:'2mm 3mm', color:'#666' }}>Status</th>
                    <th style={{ textAlign:'right', padding:'2mm 3mm', color:'#666' }}>Amount</th>
                    <th style={{ textAlign:'right', padding:'2mm 3mm', color:'#666' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {prevInvoices.map((inv, i) => (
                    <tr key={i} style={{ borderBottom:'0.5px solid #eee' }}>
                      <td style={{ padding:'2mm 3mm', textTransform:'uppercase', fontWeight:700, color:'#333' }}>{inv.provider}</td>
                      <td style={{ padding:'2mm 3mm', color:'#555' }}>{inv.description}</td>
                      <td style={{ padding:'2mm 3mm', color:'#555' }}>{fmtDate(new Date(inv.date),{month:'short',day:'numeric',year:'numeric'})}</td>
                      <td style={{ padding:'2mm 3mm', color: inv.status==='PAID' ? '#059669' : '#d97706', fontWeight:700 }}>{inv.status}</td>
                      <td style={{ padding:'2mm 3mm', textAlign:'right', fontWeight:700, color:'#111' }}>${inv.amount.toFixed(2)}</td>
                      <td className="cu-no-print" style={{ padding:'2mm 3mm', textAlign:'right' }}><button onClick={() => printSingleInvoice(inv)} style={{ background:'transparent', border:'1px solid #ccc', borderRadius:6, padding:'2px 8px', fontSize:'7.5pt', cursor:'pointer' }}>Print</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ padding:'4mm 4.5mm', background:'#f9f9f9', border:'0.5px solid #e5e5e5', borderRadius:5, marginBottom:'5mm', fontSize:'8pt', color:'#666', lineHeight:1.7 }}>
            <div style={{ fontWeight:800, color:'#333', marginBottom:4, fontSize:'8.5pt' }}>Important Notes</div>
            <div>Payments are not processed by CloudUnify. Each provider must be paid separately via their own billing portal.</div>
            <div>Costs shown reflect actual accrued charges based on live pricing data (GCP: Compute Engine API; Azure: Cost Management API).</div>
            <div>GCP costs use official us-central1 on-demand pricing. Tax, credits, and committed-use discounts are applied by each provider.</div>
            <div>This document is auto-generated by CloudUnify and serves as a cost summary, not an official invoice from any cloud provider.</div>
          </div>

          <div style={{ borderTop:'1px solid #ddd', paddingTop:'4mm', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontSize:'7.5pt', color:'#aaa', lineHeight:1.6 }}>
              <span style={{ fontWeight:800, color:'#555' }}>CloudUnify</span> &middot; cloudunify.run.place<br/>
              Generated {fmtDate(now,{month:'long',day:'numeric',year:'numeric',hour:'2-digit',minute:'2-digit'})}
            </div>
            <div style={{ textAlign:'center', fontSize:'7.5pt', color:'#aaa', lineHeight:1.6 }}>
              {invoiceNo}<br/>{period}
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:'13pt', fontWeight:900, color:'#111' }}>${fmt(grandTotal,4)}</div>
              <div style={{ fontSize:'7.5pt', color:'#aaa' }}>Total Due &middot; USD</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
